import React, { Component, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dimensions,
  ScrollView,
  StatusBar,
  View,
  Image,
  Platform,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { CountdownCircleTimer } from 'react-native-countdown-circle-timer';
import Modal from 'react-native-modal';

import Header from './Header';
import FilledButton from '../FilledButton';
import Layout from '../../constants/Layout';
import Colors from '../../constants/Colors';
import { formatPhoneNumber } from '../../utils/formatPhone';
import getInstallationIdAsync from '../../utils/getInstallationIdAsync';
import PtfKeyboardAvoidingView from '../PtfKeyboardAvoidingView';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.mediumBlue,
  },
  mainContentContainer: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: Colors.white,
  },
  signupContainer: Layout.signupContainer,
  topContainer: {
    alignItems: 'center',
    width: 330,
  },
  signupFooter: Layout.signupFooter,
  logo: {
    height: 94,
    width: 129,
    resizeMode: 'contain',
    marginTop: Layout.isSmall ? 20 : 77,
  },
  text: {
    color: Colors.veryDarkGray,
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSMall ? 20 : 26,
    marginTop: Layout.isSmall ? 14 : 53,
    width: Layout.isSmall ? 209 : 400,
  },
  inputContainer: {
    width: 328,
  },
  label: {
    color: Colors.veryDarkGray,
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSMall ? 14 : 16,
    marginTop: Layout.isSmall ? 12 : 40,
  },
  fullRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 15,
  },
  textInput: {
    color: Colors.veryDarkGray,
    fontSize: 32,
    fontFamily: 'roboto-regular',
    textAlign: 'center',
    width: 350,
    marginTop: 16,
    padding: 0,
    paddingLeft: Platform.OS === 'android' ? -4 : 20,
    letterSpacing: Platform.OS === 'android' ? 32 : 37,
    textAlign: 'left',
  },
  dashContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  dash: {
    width: 48,
    height: 1,
    marginRight: 8,
    borderBottomWidth: 1,
    borderRadius: 1,
    borderColor: Colors.mediumGray,
  },
  submitButtonContainer: {
    width: 328,
    marginTop: 24,
  },
  emptyLogo: {
    width: 24,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  timerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 28,
  },
  timeText: {
    fontSize: 12,
    fontFamily: 'roboto-regular',
  },
  errorText: {
    fontFamily: 'roboto-regular',
    color: Colors.brightRed,
  },
  getNewCodeText: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: Colors.primaryColor,
    textTransform: 'uppercase',
    textDecorationLine: 'underline',
  },
  getNewCodeDisabled: {
    marginLeft: 20,
    opacity: 0.5,
  },
  confirmText: {
    fontSize: 20,
    margin: 16,
  },
  modalContentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: Colors.white,
    width: 320,
  },
  buttonRow: {
    height: 52,
    backgroundColor: Colors.lightGrayBackground,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
    paddingRight: 8,
  },
  disabledButton: {
    padding: 10,
    backgroundColor: Colors.grayButton,
  },
  confirmButton: {
    padding: 10,
    marginBottom: 6,
    backgroundColor: Colors.primaryColor,
    borderRadius: 4,
  },
  cancelText: {
    marginTop: 10,
    marginRight: 20,
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  buttonText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: '500',
  },
});

class OneTimePassword extends Component {
  static propTypes = {
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    goNext: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    closeOtpSentAlert: PropTypes.func.isRequired,
    resendCode: PropTypes.func.isRequired,
    error: PropTypes.objectOf(PropTypes.any),
  };

  constructor(props) {
    super();
    this.state = {
      newCodeEnabled: false,
      otpCode: '',
      val: 0,
      validCode: false,
      resetCounter: new Date(),
    };
  }

  otpTimer = null;

  componentWillUnmount() {
    if (this.otpTimer) {
      clearTimeout(this.otpTimer);
    }
  }

  enableNewCode = () => {
    this.setState({ newCodeEnabled: true });
  };

  getNewCode = async () => {
    const { resendCode, registration } = this.props;
    const deviceId = await getInstallationIdAsync();
    const { phone } = registration;
    resendCode(phone, deviceId);
    this.setState({ newCodeEnabled: false, otpCode: null });
  };

  tryCode = async () => {
    const { goNext } = this.props;
    const { otpCode } = this.state;
    const deviceId = await getInstallationIdAsync();
    if (otpCode) {
      goNext(otpCode.replace(/\D/g, ''), deviceId);
    }
  };

  removeAlert = () => {
    const { closeOtpSentAlert } = this.props;
    closeOtpSentAlert();
    this.otpTimer = setTimeout(() => {
      this.otpInput.focus();
    }, 500);
  };

  render() {
    const { translation, goBack, registration, error } = this.props;
    const { showOtpSentAlert, phone } = registration;
    const { newCodeEnabled, otpCode, validCode } = this.state;

    const deviceWidth = Dimensions.get('window').width;
    const deviceHeight = Dimensions.get('window').height;

    const labels = {
      smsCode: translation.app_dialog_sms_code_title,
      cancel: translation.app_button_cancel,
      ok: translation.app_button_ok,
      newCode: translation.app_dialog_sms_code_button_send_new_code,
      error: translation.otp_error,
    };

    const onChangeCode = (text) => {
      let value = text.replace(/\D/g, '');
      let isValid = false;
      if (value.length > 2) {
        if (value.length > 6) value = value.substring(0, 6) + '';
        if (value.length === 6) isValid = true;
      }
      this.setState({ otpCode: value, validCode: isValid });
    };

    const renderTime = ({ remainingTime }) => {
      const currentTime = useRef(remainingTime);
      const prevTime = useRef(null);
      const isNewTimeFirstTick = useRef(false);

      if (currentTime.current !== remainingTime) {
        isNewTimeFirstTick.current = true;
        prevTime.current = currentTime.current;
        currentTime.current = remainingTime;
      } else {
        isNewTimeFirstTick.current = false;
      }

      // force one last re-render when the time is over to tirgger the last animation
      if (remainingTime === 0) {
        this.otpTimer = setTimeout(() => {
          const { val } = this.state;
          this.setState({ val: val + 1 });
        }, 20);
      }

      const isTimeUp = isNewTimeFirstTick.current;
      let remaingStyle = styles.remaining;
      let timeStyle = styles.time;
      if (isTimeUp) {
        remaingStyle = null;
        timeStyle = null;
      }

      return (
        <View style={styles.timeWrapper}>
          <View key={remainingTime} style={remaingStyle}>
            <Text style={styles.timeText}>{remainingTime}</Text>
          </View>
        </View>
      );
    };

    return (
      <>
        <StatusBar barStyle="light-content" backgroundColor={Colors.mediumBlue} translucent={false} />
        <SafeAreaView style={{ flex: 1 }} edges={['top', 'left', 'right', 'bottom']}>
          <PtfKeyboardAvoidingView style={{ flex: 1 }}>
            <ScrollView
              contentContainerStyle={styles.mainContentContainer}
              style={styles.container}
              testID="otp-code"
            >
              <Header title={translation.sign_up} goBack={goBack} />
              <View style={styles.signupContainer}>
                <View style={styles.topContainer}>
                  <Image
                    source={require('../../assets/images/sign_up/image_42.png')}
                    resizeMode="cover"
                    style={styles.logo}
                  />
                  <Text style={styles.text}>{translation.enter_activation_code}</Text>

                  <View style={styles.inputContainer}>
                    <Text style={styles.label}>{translation.activation_code}</Text>
                    <TextInput
                      ref={(input) => {
                        this.otpInput = input;
                      }}
                      onChangeText={onChangeCode}
                      value={otpCode}
                      keyboardType="phone-pad"
                      style={styles.textInput}
                      testID="otp"
                    />
                    <View style={styles.dashContainer}>
                      <View style={styles.dash}></View>
                      <View style={styles.dash}></View>
                      <View style={styles.dash}></View>
                      <View style={styles.dash}></View>
                      <View style={styles.dash}></View>
                      <View style={styles.dash}></View>
                    </View>
                  </View>

                  {error ? (
                    <View style={styles.errorContainer}>
                      <Text style={styles.errorText}>{labels.error}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.signupFooter}>
                  <View style={styles.submitButtonContainer}>
                    <FilledButton
                      onPress={this.tryCode}
                      label={translation.fragment_registration_language_button_next}
                      color={Colors.primaryColor}
                      full
                      textColor={Colors.white}
                      disabled={!validCode}
                      testID="button-next"
                    />
                  </View>

                  {newCodeEnabled ? (
                    <View style={styles.timerContainer}>
                      <TouchableOpacity onPress={this.getNewCode}>
                        <Text style={[styles.getNewCodeText, styles.getNewCodeEnabled]}>
                          {labels.newCode}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <View style={styles.timerContainer}>
                      <CountdownCircleTimer
                        onComplete={() => {
                          this.enableNewCode();
                        }}
                        isPlaying
                        duration={30}
                        size={24}
                        strokeWidth={2}
                        colors={[[Colors.accentColor]]}
                        trailColor={Colors.primaryColor}
                        children={() => {}}
                      >
                        {renderTime}
                      </CountdownCircleTimer>
                      <Text style={[styles.getNewCodeText, styles.getNewCodeDisabled]}>{labels.newCode}</Text>
                    </View>
                  )}
                </View>
              </View>
              <Modal
                isVisible={showOtpSentAlert}
                deviceHeight={deviceHeight}
                deviceWidth={deviceWidth}
                statusBarTranslucent
              >
                <View style={styles.modalContentContainer} testID="otp-modal">
                  <Text style={styles.confirmText}>{`${translation.code_sent_to} ${formatPhoneNumber(
                    phone
                  )}`}</Text>
                  <View style={styles.buttonRow}>
                    <TouchableOpacity
                      onPress={this.removeAlert}
                      style={styles.confirmButton}
                      testID="button-ok"
                    >
                      <Text style={styles.buttonText}>{labels.ok}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </Modal>
            </ScrollView>
          </PtfKeyboardAvoidingView>
        </SafeAreaView>
      </>
    );
  }
}

export default OneTimePassword;
